import { api } from "../base";
import type {
  IRiskWarehouseLimit,
  IRiskWarehouseHedge,
  IRiskWarehouseAccountExposure,
  IRiskWarehousePricedBalance,
} from "@pulse-ui/codegen-v2/generated_ts/risk_warehouse";

export type {
  IRiskWarehouseLimit,
  IRiskWarehouseHedge,
  IRiskWarehouseAccountExposure,
  IRiskWarehousePricedBalance,
};

// The server rejects unknown fields, so a write carries only the editable columns —
// never the full row, which also holds id/updated_at/updated_by.
export type RiskWarehouseLimitInput = Pick<
  IRiskWarehouseLimit,
  "house_account" | "asset" | "limit_usd"
>;
export type RiskWarehouseHedgeInput = Pick<
  IRiskWarehouseHedge,
  "house_account" | "asset" | "hedge_buy_usd" | "hedge_sell_usd"
>;
export interface RiskWarehouseLimitKey {
  house_account: string;
  asset: string;
}
export interface RiskWarehouseHedgeKey {
  house_account: string;
  asset: string;
}
export interface RiskWarehouseBalanceParams {
  house_account?: string;
  asset?: string;
}

const LIMIT_URL = "/api/risk/v1/risk-warehouse-limit";
const HEDGE_URL = "/api/risk/v1/risk-warehouse-hedge";
const BALANCE_URL = "/api/risk/v1/risk-warehouse-balance";

export const riskWarehouseEndpoints = api.injectEndpoints({
  endpoints: (build) => ({
    getRiskWarehouseLimits: build.query<
      IRiskWarehouseLimit[],
      { house_account?: string; asset?: string } | void
    >({
      query: (params) => ({ url: LIMIT_URL, params: params ?? undefined }),
      providesTags: ["RiskWarehouseLimits"],
    }),
    createRiskWarehouseLimit: build.mutation<IRiskWarehouseLimit, RiskWarehouseLimitInput>({
      query: (limit) => ({ url: LIMIT_URL, method: "POST", body: limit }),
      invalidatesTags: ["RiskWarehouseLimits"],
    }),
    updateRiskWarehouseLimit: build.mutation<IRiskWarehouseLimit, RiskWarehouseLimitInput>({
      query: (limit) => ({ url: LIMIT_URL, method: "PUT", body: limit }),
      invalidatesTags: ["RiskWarehouseLimits"],
    }),
    deleteRiskWarehouseLimit: build.mutation<void, RiskWarehouseLimitKey>({
      query: ({ house_account, asset }) => ({
        url: LIMIT_URL,
        method: "DELETE",
        params: { house_account, asset },
      }),
      invalidatesTags: ["RiskWarehouseLimits"],
    }),
    getRiskWarehouseHedges: build.query<
      IRiskWarehouseHedge[],
      { house_account?: string; asset?: string } | void
    >({
      query: (params) => ({ url: HEDGE_URL, params: params ?? undefined }),
      providesTags: ["RiskWarehouseHedges"],
    }),
    // Hedge writes move effective_usd, so the priced balances go stale with them.
    createRiskWarehouseHedge: build.mutation<IRiskWarehouseHedge, RiskWarehouseHedgeInput>({
      query: (hedge) => ({ url: HEDGE_URL, method: "POST", body: hedge }),
      invalidatesTags: ["RiskWarehouseHedges", "RiskWarehouseBalances"],
    }),
    updateRiskWarehouseHedge: build.mutation<IRiskWarehouseHedge, RiskWarehouseHedgeInput>({
      query: (hedge) => ({ url: HEDGE_URL, method: "PUT", body: hedge }),
      invalidatesTags: ["RiskWarehouseHedges", "RiskWarehouseBalances"],
    }),
    deleteRiskWarehouseHedge: build.mutation<void, RiskWarehouseHedgeKey>({
      query: ({ house_account, asset }) => ({
        url: HEDGE_URL,
        method: "DELETE",
        params: { house_account, asset },
      }),
      invalidatesTags: ["RiskWarehouseHedges", "RiskWarehouseBalances"],
    }),
    getRiskWarehouseBalances: build.query<
      IRiskWarehouseAccountExposure[],
      RiskWarehouseBalanceParams | void
    >({
      query: (params) => ({ url: BALANCE_URL, params: params ?? undefined }),
      providesTags: ["RiskWarehouseBalances"],
    }),
  }),
});

export const {
  useGetRiskWarehouseLimitsQuery,
  useCreateRiskWarehouseLimitMutation,
  useUpdateRiskWarehouseLimitMutation,
  useDeleteRiskWarehouseLimitMutation,
  useGetRiskWarehouseHedgesQuery,
  useCreateRiskWarehouseHedgeMutation,
  useUpdateRiskWarehouseHedgeMutation,
  useDeleteRiskWarehouseHedgeMutation,
  useGetRiskWarehouseBalancesQuery,
} = riskWarehouseEndpoints;
